import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectGamePlayers } from "src/store/game"

import UnitItem from "src/components/UnitItem"

import "./BattleUnitsSummary.css"

/* The battle's container (owner, 2026-09-07): one boxed block whatever the battle's state, with a
   headline that says what is happening — "an epic battle rages" while it burns, who holds the
   field once it ended — the round switcher in its corner, and one column per team with both
   ledgers in each: who still stands, and what has fallen so far — chips with faces, totals beside
   the labels. Losses-so-far rather than the starting roster, because this block travels with
   every round view: standing plus fallen at any point in the fight is self-explaining, where
   "walked in with" only made sense at the start.

   Under the fallen, per player, the three numbers the owner asked for: the meat a zombie's kills
   paid (U5 — always, even zero, because a zombie player is always asking), the humans who got up
   again on that zombie's side (2026-09-10, beside the meat and on the same terms), and the dead a
   human's medics got back on their feet (only when medics stood there or somebody came back; a
   player with no medics is not asking). All three are the server's numbers, never re-derived here.

   The last of those counted *health points* until 2026-09-14, when the medic stopped healing and
   started reviving. Both figures on the bill are bodies now, which is what lets a player read the
   two columns against each other — what the horde took off the field against what humanity put
   back on it.

   There is no separate report modal and no report card any more — the battle view is the report,
   and this is its top. Sharing lives in the view's header, beside the help and close buttons. */

const BattleUnitsSummary = ({
  title,
  tone = "live",
  headerRight = null,
  remainingByUser,
  lossesByUser,
  meatByUser = {},
  revivedByUser = {},
  raisedByUser = {},
  ended = false,
}) => {
  const { t } = useTranslation()
  const players = useSelector(selectGamePlayers)

  const teamOfId = (id) => players.find((p) => p.user.id === Number(id))?.team
  const nameOf = (id) => players.find((p) => p.user.id === Number(id))?.user.username ?? `#${id}`
  const clean = (byUnit) =>
    Object.fromEntries(Object.entries(byUnit || {}).filter(([, count]) => Number(count) > 0).map(([u, c]) => [u, Number(c)]))
  const sum = (byUnit) => Object.values(byUnit).reduce((total, count) => total + count, 0)

  // Per player, not merged per side (owner's R25, 2026-09-03): a 2v2 battle summed both allies'
  // armies into one strip, so nobody could tell how many of the bodies were theirs.
  const ids = [
    ...new Set([
      ...Object.keys(remainingByUser || {}),
      ...Object.keys(lossesByUser || {}),
    ]),
  ]
  const byTeam = { human: [], zombie: [] }
  ids.forEach((id) => {
    const team = teamOfId(id)
    if (!byTeam[team]) return
    const standing = clean(remainingByUser?.[id])
    const lost = clean(lossesByUser?.[id])
    if (Object.keys(standing).length === 0 && Object.keys(lost).length === 0) return
    const revived = Number(revivedByUser?.[id] ?? 0)
    byTeam[team].push({
      id,
      standing,
      lost,
      meat: team === "zombie" ? Number(meatByUser?.[id] ?? 0) : null,
      // Beside the meat, and on the same terms (owner, 2026-09-10): a zombie player is always
      // asking what this fight fed them, and the bodies that got up are the other half of the
      // answer. Always shown for a zombie, zero included — a raise that came up short is a real
      // outcome, and a figure that only appears when it is good reads as a bug when it vanishes.
      raised: team === "zombie" ? Number(raisedByUser?.[id] ?? 0) : null,
      revived: team === "human" && (revived > 0 || "medic" in standing) ? revived : null,
    })
  })
  const teams = ["human", "zombie"].filter((teamKey) => byTeam[teamKey].length > 0)

  const strip = (labelKey, units, kind) => {
    const total = sum(units)
    return (
      <div className={`battle-units-strip ${kind}`}>
        <p className="section-label">
          {t(labelKey)}
          <span className="battle-units-total">{total}</span>
        </p>
        <div className="battle-units-chips">
          {Object.entries(units)
            .sort(([, a], [, b]) => b - a)
            .map(([unit, count]) => (
              <UnitItem key={unit} type={unit} count={count} showLabel />
            ))}
          {total === 0 && <span className="battle-units-none">—</span>}
        </div>
      </div>
    )
  }

  return (
    <div className={`battle-units-summary ${tone}`}>
      <header className="battle-units-head">
        <span className="battle-units-headline">{title}</span>
        {headerRight}
      </header>
      {teams.length > 0 && (
        <div className="battle-units-columns">
          {teams.map((teamKey) => (
            <div className={`battle-units-column ${teamKey}`} key={teamKey}>
              <p className="battle-units-team">{t(`game.battle.${teamKey}`)}</p>
              {byTeam[teamKey].map(({ id, standing, lost, meat, revived, raised }) => (
                <div className="battle-units-player" key={id}>
                  <p className="battle-units-name">
                    {nameOf(id)}
                  </p>
                  {strip(ended ? "game.battle.standingEnd" : "game.battle.standingNow", standing, "now")}
                  {strip(ended ? "game.battle.lostEnd" : "game.battle.lostSoFar", lost, "lost")}
                  {meat != null && (
                    <p className="battle-units-figure meat" title={t("game.battleReport.meatWonTitle")}>
                      {t(ended ? "game.battle.meatWon" : "game.battle.meatSoFar")}
                      <span className="battle-units-total">{t("game.battle.meatPoints", { meat })}</span>
                    </p>
                  )}
                  {raised != null && (
                    <p className="battle-units-figure raised" title={t("game.battleReport.raisedTitle")}>
                      {t(ended ? "game.battle.raised" : "game.battle.raisedSoFar")}
                      <span className="battle-units-total">{t("game.battle.raisedBodies", { bodies: raised })}</span>
                    </p>
                  )}
                  {revived != null && (
                    <p className="battle-units-figure revived" title={t("game.battle.revivedTitle")}>
                      {t(ended ? "game.battle.revived" : "game.battle.revivedSoFar")}
                      <span className="battle-units-total">{t("game.battle.revivedBodies", { bodies: revived })}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default BattleUnitsSummary
